import { cn } from '@/context/global/utils/cn';
import { useEffect, useRef, useState } from 'react';

/**
 * Default debounce delay in milliseconds
 */
const DEFAULT_DEBOUNCE_DELAY = 300;

export interface ICWInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  className?: string;
  debounceDelay?: number;
  onChange?: (value: string) => void;
  onDebouncedChange?: (value: string) => void;
  prefixIcon?: React.ReactNode;
}

const CWInput = ({
  className,
  debounceDelay = DEFAULT_DEBOUNCE_DELAY,
  onChange,
  onDebouncedChange,
  value: controlledValue,
  prefixIcon,
  ...rest
}: ICWInputProps) => {
  const [internalValue, setInternalValue] = useState(controlledValue ?? '');
  const onDebouncedChangeRef = useRef(onDebouncedChange);

  // Keep ref updated with latest callback
  useEffect(() => {
    onDebouncedChangeRef.current = onDebouncedChange;
  }, [onDebouncedChange]);

  // Sync with controlled value
  useEffect(() => {
    if (controlledValue !== undefined) {
      setInternalValue(controlledValue);
    }
  }, [controlledValue]);

  // Debounce effect - only triggers on value change, not callback change
  useEffect(() => {
    const timer = setTimeout(() => {
      onDebouncedChangeRef.current?.(String(internalValue));
    }, debounceDelay);

    return () => clearTimeout(timer);
  }, [internalValue, debounceDelay]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    onChange?.(newValue);
  };

  return (
    <div
      className={cn(
        'flex items-center rounded-lg border-2 border-neutral-800 px-4 py-3',
        className,
      )}
    >
      {prefixIcon && <span className="mr-2">{prefixIcon}</span>}
      <input
        className="w-full bg-transparent focus:outline-none"
        value={internalValue}
        onChange={handleChange}
        {...rest}
      />
    </div>
  );
};

export default CWInput;
